import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { toGermanNumberFormat } from "@helpers";
import { DonutChartSkeleton } from "./DonutChartSkeleton";
import { useDonutChartOptions } from "./useDonutChartOptions";
import { useDonutHoverSync } from "./useDonutHoverSync";
import type { ClassificationBreakdownData } from "@components";

export interface ClassificationDonutChartProps {
  data: ClassificationBreakdownData[];
  ringTotalValue: number;
  centerSubtitle: string;
  isLoading?: boolean;
  hoveredItem?: ClassificationBreakdownData | null;
  onSectionClick: (item: ClassificationBreakdownData) => void;
  onSectionHover: (item: ClassificationBreakdownData | null) => void;
  isItemClickable?: (item: ClassificationBreakdownData) => boolean;
}

export const ClassificationDonutChart = ({
  data,
  ringTotalValue,
  centerSubtitle,
  isLoading = false,
  hoveredItem,
  onSectionClick,
  onSectionHover,
  isItemClickable,
}: ClassificationDonutChartProps) => {
  const isLessMd = useMediaQuery((t: Theme) => t.breakpoints.down("md"));
  const isXsDown = useMediaQuery((t: Theme) => t.breakpoints.down("sm"));
  const chartHeight = isXsDown ? 340 : isLessMd ? 450 : 500;

  const chartRef = useDonutHoverSync(data, hoveredItem);
  const chartOptions = useDonutChartOptions(
    data,
    chartHeight,
    onSectionClick,
    onSectionHover,
    isItemClickable
  );

  const centerLine1 =
    ringTotalValue > 0 ? `${toGermanNumberFormat(ringTotalValue)}€` : "—";

  if (isLoading) {
    return <DonutChartSkeleton chartHeight={chartHeight} />;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: chartHeight,
        height: chartHeight,
      }}
    >
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={chartOptions}
      />
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          pointerEvents: "none",
          maxWidth: "35%",
          overflow: "hidden",
        }}
      >
        <Typography
          color="text.primary"
          variant={isXsDown ? "h6" : centerLine1.length > 9 ? "h4" : "h3"}
        >
          {centerLine1}
        </Typography>
        {centerSubtitle && (
          <Typography
            color="text.secondary"
            variant={isXsDown ? "body2" : "subtitle1"}
            noWrap
            sx={{ mt: 0.5, maxWidth: "100%" }}
          >
            {centerSubtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
